import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Database, HardDrive, Server, Trash2, RefreshCw, Download, AlertTriangle, Activity, Save, ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const fmt = (b) => {
  b = b || 0;
  if (b >= 1e9) return (b / 1e9).toFixed(2) + " GB";
  if (b >= 1e6) return (b / 1e6).toFixed(1) + " MB";
  if (b >= 1e3) return (b / 1e3).toFixed(1) + " KB";
  return b + " B";
};
const CAT_COLOR = { originals: "#00F0FF", optimized: "#8B5CF6", proxy: "#22c55e", exports: "#FFC53D", user_images: "#ec4899", user_audio: "#f97316", ai_cutouts: "#06b6d4" };
const SAFE_STYLE = { safe: ["Safe to delete", "#22c55e"], possibly: ["Possibly safe", "#f59e0b"], active: ["Do not delete", "#ef4444"] };

const Card = ({ label, value, sub, accent }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid={`storage-card-${label}`}>
    <div className="text-xs text-[#A1A1AA]">{label}</div>
    <div className="mt-1 text-2xl font-bold" style={{ color: accent || "#fff" }}>{value}</div>
    {sub && <div className="mt-0.5 text-[11px] text-[#666]">{sub}</div>}
  </div>
);

export default function StorageMedia() {
  const [data, setData] = useState(null);
  const [health, setHealth] = useState(null);
  const [cleanup, setCleanup] = useState(null);
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [confirmKey, setConfirmKey] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [err, setErr] = useState(null);

  const load = async (refresh = 0) => {
    setBusy(true);
    setErr(null);
    // Resilient: each metric loads independently — one failing (e.g. slow object-storage
    // provider) must never leave the whole dashboard stuck on "Loading…".
    const [ovR, hR, clR] = await Promise.allSettled([
      api.adminStorageOverview(refresh), api.adminStorageHealth(), api.adminStorageCleanupScan(),
    ]);
    if (ovR.status === "fulfilled") {
      const v = ovR.value;
      if (v && v.status === "error" && !v.snapshot) {
        // Backend reported a real failure (not a UI issue) — surface it, keep other panels usable.
        setErr(v.message || (v.errors && v.errors[0]?.message) || "Storage usage could not be calculated.");
        setData((d) => d || null);
      } else {
        setData(v); setSettings(v.settings);
      }
    } else { setErr(ovR.reason?.response?.data?.detail || ovR.reason?.message || "Storage overview unavailable"); }
    if (hR.status === "fulfilled") setHealth(hR.value); else setHealth((h) => h || { error: "unavailable" });
    if (clR.status === "fulfilled") setCleanup(clR.value); else setCleanup((c) => c || { error: "unavailable" });
    setLastUpdated(new Date());
    if (ovR.status === "rejected") toast.error("Couldn't load storage overview — showing what's available.");
    setBusy(false);
  };
  useEffect(() => { load(0); }, []);

  const saveSettings = async () => {
    try { const r = await api.adminSaveStorageSettings(settings); setSettings(r.settings); toast.success("Storage settings saved"); }
    catch { toast.error("Save failed"); }
  };
  const runCleanup = async (key) => {
    try {
      const r = await api.adminStorageCleanupRun(key, confirmText);
      toast.success(`Freed ${fmt(r.freed)} · ${r.deleted} item(s)`);
      setConfirmKey(null); setConfirmText(""); load(1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Cleanup failed"); }
  };
  const exportCsv = () => {
    if (!data) return;
    const rows = [["Email", "Plan", "Projects", "Files", ...Object.keys(CAT_COLOR), "Total (bytes)"]];
    data.snapshot.users.forEach((u) => rows.push([u.email, u.plan, u.projects, u.files, ...Object.keys(CAT_COLOR).map((k) => u[k] || 0), u.size]));
    const csv = rows.map((r) => r.join(",")).join("\n");
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "storage_by_user.csv"; a.click();
  };

  if (!data) return (
    <div className="p-6" data-testid="storage-loading">
      {busy ? (
        <div className="flex items-center gap-2 text-[#A1A1AA]"><Loader2 size={16} className="animate-spin text-[#00F0FF]" /> Loading storage analytics…</div>
      ) : (
        <div className="max-w-md rounded-xl border border-red-400/30 bg-red-500/[0.06] p-5" data-testid="storage-error">
          <div className="mb-1 font-display text-base font-bold text-white">Couldn't load storage overview</div>
          <p className="mb-3 text-sm text-[#A1A1AA]">{err || "The storage provider did not respond."} You can retry — other admin pages are unaffected.</p>
          <button onClick={() => load(1)} data-testid="storage-retry-btn" className="rounded-lg bg-[#00F0FF] px-4 py-2 text-sm font-bold text-black hover:bg-[#00F0FF]/90">Retry</button>
        </div>
      )}
    </div>
  );
  const s = data.snapshot;
  const disk = s.disks.server_disk || {};
  const healthColor = { healthy: "#22c55e", warning: "#f59e0b", critical: "#ef4444" }[health?.status] || "#A1A1AA";

  return (
    <div className="space-y-6" data-testid="storage-media">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-bold text-white"><HardDrive size={20} className="text-[#00F0FF]" /> Storage &amp; Media</h2>
          <p className="mt-1 text-xs text-[#666]">Last updated {(lastUpdated || new Date()).toLocaleString()} · Real storage consumption. {(health?.error || cleanup?.error) && <span className="text-[#f59e0b]">Some sub-metrics are temporarily unavailable.</span>}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={exportCsv} data-testid="storage-csv-btn" className="flex items-center gap-1 rounded-lg border border-white/15 px-3 py-1.5 text-xs text-[#A1A1AA] hover:text-white"><Download size={13} /> CSV</button>
          <button onClick={() => load(1)} disabled={busy} data-testid="storage-refresh-btn" className="flex items-center gap-1 rounded-lg bg-[#00F0FF] px-3 py-1.5 text-xs font-semibold text-black hover:brightness-110 disabled:opacity-50"><RefreshCw size={13} className={busy ? "animate-spin" : ""} /> Refresh Now</button>
        </div>
      </div>

      {/* provider + capacity honesty */}
      <div className="rounded-xl border border-[#00F0FF]/20 bg-[#00F0FF]/[0.04] p-4 text-xs text-[#A1A1AA]" data-testid="storage-provider">
        <div className="flex flex-wrap gap-x-8 gap-y-1">
          <span><b className="text-white">Provider:</b> {s.provider.name}</span>
          <span><b className="text-white">Type:</b> {s.provider.type}</span>
          <span><b className="text-white">Prefix:</b> {s.provider.app_prefix}</span>
          <span><b className="text-white">Endpoint:</b> {s.provider.endpoint}</span>
        </div>
        <p className="mt-2 text-[#FFC53D]">⚠ {s.provider.quota_note}</p>
      </div>

      {/* component health diagnostics (Part A) */}
      {data.components && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-white/10 bg-white/[0.02] p-3 text-xs" data-testid="storage-components">
          {data.status && <span className={`mr-1 rounded-full px-2 py-0.5 font-bold ${data.status === "ok" ? "bg-[#22c55e]/15 text-[#22c55e]" : data.status === "partial" ? "bg-[#f59e0b]/15 text-[#f59e0b]" : "bg-red-500/15 text-red-400"}`}>{data.status.toUpperCase()}</span>}
          {Object.entries(data.components).map(([k, v]) => {
            const c = v === "ok" ? "#22c55e" : v === "unavailable" ? "#f59e0b" : v === "error" ? "#ef4444" : "#A1A1AA";
            return <span key={k} className="flex items-center gap-1 text-[#A1A1AA]" data-testid={`storage-comp-${k}`}><span className="h-1.5 w-1.5 rounded-full" style={{ background: c }} />{k.replace(/_/g, " ")}: <span style={{ color: c }}>{v}</span></span>;
          })}
          {data.errors && data.errors.length > 0 && <span className="text-[#f59e0b]" data-testid="storage-errors">· {data.errors.length} note(s)</span>}
        </div>
      )}

      {/* overview cards */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Card label="Tracked Media" value={fmt(s.tracked.total)} sub={`${s.tracked.files} files · ${s.tracked.unknown_size_files} unknown size`} accent="#00F0FF" />
        <Card label="Server Disk Used" value={disk.pct != null ? disk.pct + "%" : "—"} sub={`${fmt(disk.used)} / ${fmt(disk.total)} · ${fmt(disk.free)} free`} accent="#8B5CF6" />
        <Card label="Database" value={fmt(s.database.data_size)} sub={`storage ${fmt(s.database.storage_size)} · idx ${fmt(s.database.index_size)}`} accent="#FFC53D" />
        <Card label="Storage Multiplier" value={s.lineage.multiplier ? s.lineage.multiplier + "×" : "—"} sub={`orig ${fmt(s.lineage.original)} → +derived ${fmt(s.lineage.derived)}`} accent="#22c55e" />
      </div>

      {/* health + growth */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="storage-health">
          <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-white"><Activity size={15} style={{ color: healthColor }} /> Storage Health: <span style={{ color: healthColor }}>{(health?.status || "").toUpperCase()}</span></h3>
          <div className="space-y-1">
            {(health?.checks || []).map((c, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="text-[#A1A1AA]">{c.name}</span>
                <span style={{ color: c.ok ? "#22c55e" : "#ef4444" }}>{c.ok ? "OK" : "Attention"} {c.detail ? `· ${c.detail}` : ""}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="storage-growth">
          <h3 className="mb-2 text-sm font-semibold text-white">Growth &amp; Forecast</h3>
          <div className="grid grid-cols-3 gap-2 text-center">
            {["7d", "30d", "90d"].map((k) => (
              <div key={k}><div className="text-lg font-bold text-white">{data.growth[k] != null ? "+" + fmt(data.growth[k]) : "—"}</div><div className="text-[10px] text-[#666]">last {k}</div></div>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-[#666]">{data.forecast.enough_data ? `≈ ${fmt(data.forecast.bytes_per_day)}/day — ${data.forecast.note}` : data.forecast.note}</p>
        </div>
      </div>

      {/* category breakdown */}
      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="storage-categories">
        <h3 className="mb-3 text-sm font-semibold text-white">Storage by Category</h3>
        <div className="mb-3 flex h-3 w-full overflow-hidden rounded-full bg-white/5">
          {s.categories.filter((c) => c.size > 0).map((c) => <div key={c.key} style={{ width: `${c.pct}%`, background: CAT_COLOR[c.key] }} title={`${c.label} ${c.pct}%`} />)}
        </div>
        <table className="w-full text-left text-xs">
          <thead className="text-[#A1A1AA]"><tr><th className="py-1">Category</th><th>Files</th><th>Size</th><th>%</th><th>Unknown</th></tr></thead>
          <tbody className="text-white">
            {s.categories.map((c) => (
              <tr key={c.key} className="border-t border-white/5">
                <td className="py-1.5"><span className="mr-2 inline-block h-2.5 w-2.5 rounded-full" style={{ background: CAT_COLOR[c.key] }} />{c.label}</td>
                <td>{c.files}</td><td>{fmt(c.size)}</td><td>{c.pct}%</td><td className="text-[#666]">{c.unknown || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* per user + project drilldown */}
      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="storage-users">
        <h3 className="mb-3 text-sm font-semibold text-white">Storage by User <span className="text-[11px] font-normal text-[#666]">(avg {fmt(data.snapshot.per_user.average)} · median {fmt(data.snapshot.per_user.median)})</span></h3>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="text-[#A1A1AA]"><tr><th className="py-1 pr-3">User</th><th>Plan</th><th>Projects</th><th>Files</th><th>Originals</th><th>Optimized</th><th>Exports</th><th>Total</th></tr></thead>
            <tbody className="text-white">
              {s.users.map((u) => (
                <tr key={u.user_id} className="border-t border-white/5">
                  <td className="max-w-[180px] truncate py-1.5 pr-3">{u.email}</td>
                  <td className="capitalize">{u.plan}</td><td>{u.projects}</td><td>{u.files}</td>
                  <td>{fmt(u.originals)}</td><td>{fmt(u.optimized)}</td><td>{fmt(u.exports)}</td>
                  <td className="font-semibold text-[#00F0FF]">{fmt(u.size)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="storage-projects">
        <h3 className="mb-3 text-sm font-semibold text-white">Largest Projects</h3>
        <div className="space-y-1">
          {s.projects.slice(0, 25).map((p) => (
            <div key={p.project_id} className="rounded-lg border border-white/5">
              <button onClick={() => setExpanded(expanded === p.project_id ? null : p.project_id)} className="flex w-full items-center justify-between px-3 py-2 text-xs hover:bg-white/[0.03]" data-testid={`storage-project-${p.project_id}`}>
                <span className="flex items-center gap-1 text-white">{expanded === p.project_id ? <ChevronDown size={13} /> : <ChevronRight size={13} />}{p.name}{p.is_demo && <span className="ml-1 rounded bg-white/10 px-1 text-[9px] text-[#A1A1AA]">demo</span>}</span>
                <span className="font-semibold text-[#00F0FF]">{fmt(p.total)}</span>
              </button>
              {expanded === p.project_id && (
                <div className="border-t border-white/5 px-3 py-2 text-[11px] text-[#A1A1AA]">
                  <div className="mb-1 text-[10px] uppercase tracking-wide text-[#666]">Lineage: original → optimized → proxy → export</div>
                  <div className="flex flex-wrap gap-x-6 gap-y-1">
                    {["originals", "optimized", "proxy", "user_images", "user_audio", "ai_cutouts", "exports"].map((k) => (p[k] ? <span key={k}>{k}: <b className="text-white">{fmt(p[k])}</b></span> : null))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* file types */}
      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="storage-filetypes">
        <h3 className="mb-3 text-sm font-semibold text-white">By File Type</h3>
        <div className="flex flex-wrap gap-2">
          {s.file_types.map((f) => (
            <div key={f.ext} className="rounded-lg border border-white/10 px-3 py-1.5 text-xs">
              <span className="font-semibold uppercase text-white">{f.ext}</span> <span className="text-[#00F0FF]">{fmt(f.size)}</span> <span className="text-[#666]">· {f.files} · avg {fmt(f.avg)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* cleanup */}
      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="storage-cleanup">
        <h3 className="mb-1 flex items-center gap-2 text-sm font-semibold text-white"><Trash2 size={15} className="text-[#ef4444]" /> Temporary / Orphaned Storage &amp; Cleanup</h3>
        <p className="mb-3 text-[11px] text-[#666]">Recoverable now: <b className="text-[#22c55e]">{fmt(cleanup?.recoverable)}</b>. {cleanup?.protected_note}</p>
        <div className="space-y-2">
          {(cleanup?.groups || []).map((g) => {
            const [lbl, color] = SAFE_STYLE[g.safety] || SAFE_STYLE.active;
            return (
              <div key={g.key} className="rounded-lg border border-white/10 p-3" data-testid={`cleanup-${g.key}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs font-semibold text-white">{g.label} <span className="ml-2 rounded-full px-2 py-0.5 text-[10px]" style={{ color, background: `${color}22` }}>{lbl}</span></div>
                    <div className="text-[11px] text-[#666]">{g.files} file(s) · {fmt(g.size)} · {g.note}</div>
                  </div>
                  {g.size > 0 && confirmKey !== g.key && (
                    <button onClick={() => { setConfirmKey(g.key); setConfirmText(""); }} data-testid={`cleanup-start-${g.key}`} className="rounded-lg border border-[#ef4444]/40 px-3 py-1.5 text-xs font-semibold text-[#ef4444] hover:bg-[#ef4444]/10">Clean up</button>
                  )}
                </div>
                {confirmKey === g.key && (
                  <div className="mt-3 rounded-lg border border-[#ef4444]/30 bg-[#ef4444]/[0.05] p-3">
                    <p className="text-xs text-white">These files will be <b>permanently deleted</b> ({fmt(g.size)} freed). Type <b>DELETE STORAGE</b> to confirm.</p>
                    <div className="mt-2 flex gap-2">
                      <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="DELETE STORAGE" data-testid={`cleanup-confirm-input-${g.key}`}
                        className="flex-1 rounded-lg border border-white/10 bg-[#1a1a1a] px-3 py-1.5 text-xs text-white outline-none" />
                      <button onClick={() => runCleanup(g.key)} disabled={confirmText !== "DELETE STORAGE"} data-testid={`cleanup-confirm-${g.key}`} className="rounded-lg bg-[#ef4444] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40">Delete</button>
                      <button onClick={() => { setConfirmKey(null); setConfirmText(""); }} className="rounded-lg border border-white/15 px-3 py-1.5 text-xs text-[#A1A1AA]">Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* settings */}
      {settings && (
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="storage-settings">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-white"><AlertTriangle size={15} className="text-[#FFC53D]" /> Alert Thresholds · Retention · Cost</h3>
            <button onClick={saveSettings} data-testid="storage-settings-save" className="flex items-center gap-1 rounded-lg bg-[#00F0FF] px-3 py-1.5 text-xs font-semibold text-black hover:brightness-110"><Save size={13} /> Save</button>
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {[["alert_warn_pct", "Warn at %"], ["alert_high_pct", "High at %"], ["alert_critical_pct", "Critical at %"], ["cost_per_gb_month", "$/GB-month (0=unknown)"], ["retain_failed_render_days", "Failed render retain (days)"], ["retain_incomplete_upload_hours", "Incomplete upload retain (hrs)"], ["retain_media_cache_days", "Media cache retain (days)"], ["retain_orphan_export_days", "Orphan export retain (days)"]].map(([k, l]) => (
              <label key={k} className="block">
                <span className="text-[11px] text-[#A1A1AA]">{l}</span>
                <input type="number" value={settings[k]} onChange={(e) => setSettings((p) => ({ ...p, [k]: +e.target.value }))} data-testid={`storage-set-${k}`}
                  className="mt-1 w-full rounded-lg border border-white/10 bg-[#1a1a1a] px-3 py-1.5 text-sm text-white outline-none focus:border-[#00F0FF]" />
              </label>
            ))}
          </div>
          {data.cost && <p className="mt-3 text-[11px] text-[#666]">Cost {data.cost.label}: ≈ ${data.cost.monthly}/mo at ${data.cost.per_gb_month}/GB · +${data.cost.monthly_growth}/mo growth.</p>}
        </div>
      )}

      {/* server disk vs object vs db */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3" data-testid="storage-systems">
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4"><h4 className="flex items-center gap-1 text-xs font-semibold text-white"><HardDrive size={13} className="text-[#00F0FF]" /> Object Storage (tracked)</h4><div className="mt-1 text-lg font-bold text-white">{fmt(s.tracked.total)}</div><div className="text-[10px] text-[#666]">Provider quota not exposed</div></div>
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4"><h4 className="flex items-center gap-1 text-xs font-semibold text-white"><Server size={13} className="text-[#8B5CF6]" /> Server Disk</h4><div className="mt-1 text-lg font-bold text-white">{fmt(disk.used)} / {fmt(disk.total)}</div><div className="text-[10px] text-[#666]">cache {fmt(s.disks.media_cache.size)} · temp {fmt(s.disks.render_temp.size)}</div></div>
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4"><h4 className="flex items-center gap-1 text-xs font-semibold text-white"><Database size={13} className="text-[#FFC53D]" /> Database</h4><div className="mt-1 text-lg font-bold text-white">{fmt(s.database.data_size)}</div><div className="text-[10px] text-[#666]">{s.database.top_collections.slice(0, 3).map((c) => c.name).join(", ")}</div></div>
      </div>
    </div>
  );
}
